import { gql } from '@apollo/client'
import dayjs, { type Dayjs } from 'dayjs'
import { print } from 'graphql'

import { notifyBugsnag } from '../bugsnag'
import type { ActionLog, Organization, Token, User } from './types'

/**
 * マウス・フォーカスのいずれの操作もなく経過した時間がこの秒数を超えると、
 * セッションは非アクティブとみなして打ち切り、経過分を計測時間から差し引きます。
 */
const INACTIVE_JUDGE_TIME = 180

const CREATE_ACTION_LOG_MUTATION = gql`
  mutation createActionLog(
    $organizationId: ID!
    $userId: ID!
    $organizationName: String!
    $userName: String!
    $path: String!
    $actionLog: ActionLogInput!
  ) {
    createActionLog(
      organizationId: $organizationId
      userId: $userId
      organizationName: $organizationName
      userName: $userName
      path: $path
      actionLog: $actionLog
    ) {
      id
    }
  }
`

/**
 * 利用時間 (session) と各種アクションを SALESCORE トラッカーサーバへ送信するシングルトン。
 *
 * セッションは画面上のマウス操作 / フォーカスをトリガに開始し、画面の離脱 (unload)、
 * フォーカス喪失 (blur)、または一定時間の無操作のいずれかで終了したものとして集計します。
 * 集計値は GraphQL mutation (`createActionLog`) で `apps/backend/tracker` に送信され、
 * トラッカー DB の `action_log` テーブルに保存されます。
 *
 * GraphQL の送信は `navigator.sendBeacon` を使うことで、画面遷移時のログ消失を防いでいます。
 */
class Tracker {
  private token: Token = undefined
  private organization: Organization = { id: 'unknown', name: 'unknown' }
  private user: User = { id: 'unknown', name: 'unknown' }
  private sessionStartAt: Dayjs | undefined = undefined
  // マウス操作、またはフォーカスが当たった際にアクティブとみなす
  private lastActiveMotionAt: Dayjs = dayjs()

  private static instance: Tracker

  private constructor() {
    window.addEventListener('unload', () => {
      this.onUnload()
    })

    window.addEventListener('focus', () => {
      this.onFocus()
    })

    window.addEventListener('blur', () => {
      this.onBlur()
    })

    globalThis.addEventListener('mousemove', () => {
      this.onMouseMove()
    })

    setInterval(() => {
      this.onActivityCheck()
    }, 10 * 1000)
  }

  public static shared(): Tracker {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    Tracker.instance ??= new Tracker()
    return Tracker.instance
  }

  public track(actionLog: ActionLog) {
    if (process.env.TRACKER_GRAPHQL_URL === undefined) {
      return
    }

    const headers: { type: string; authorization?: string } = { type: 'application/json' }
    if (this.token !== undefined) {
      headers.authorization = `Bearer ${this.token}`
    }

    const blob = new Blob(
      [
        JSON.stringify({
          query: print(CREATE_ACTION_LOG_MUTATION),
          variables: {
            organizationId: this.organization.id,
            userId: this.user.id,
            organizationName: this.organization.name,
            userName: this.user.name,
            path: globalThis.location.pathname,
            actionLog,
          },
        }),
      ],
      headers,
    )

    try {
      navigator.sendBeacon(process.env.TRACKER_GRAPHQL_URL, blob)
    } catch (error) {
      if (error instanceof Error) {
        notifyBugsnag({ error, severity: 'warning' })
      }
    }
  }

  public setToken(token: Token) {
    this.token = token
  }

  public setUser(organization: Organization, user: User) {
    this.organization = organization
    this.user = user
    this.startSession()
  }

  private startSession() {
    if (this.sessionStartAt !== undefined) {
      return
    }
    this.sessionStartAt = dayjs()
  }

  private getSessionTime(): number | undefined {
    if (this.sessionStartAt === undefined) {
      return undefined
    }
    const time = dayjs().diff(this.sessionStartAt, 'second', false)
    this.sessionStartAt = undefined
    return time
  }

  private trackSessionTime(excessTime = 0) {
    const sessionTime = this.getSessionTime()
    if (sessionTime === undefined) {
      return
    }
    // 非アクティブ時など余剰な時間をセッション時間から差し引く
    const actualSessionTime = sessionTime - excessTime

    // NOTE: actualSessionTimeが負の値になることは想定しない動きだが、
    // ログイン時間がマイナスになってしまうという報告を受けて暫定の止血対応
    // c.f. https://github.com/salescore-inc/issues/issues/968
    if (actualSessionTime <= 0) {
      return
    }

    this.track({ category: 'session', value: actualSessionTime.toString() })
  }

  private onUnload() {
    this.trackSessionTime()
  }

  private onFocus() {
    this.lastActiveMotionAt = dayjs()
    this.startSession()
  }

  private onBlur() {
    this.trackSessionTime()
  }

  private onMouseMove() {
    this.lastActiveMotionAt = dayjs()
    // 放置後、sessionStartAtがundefinedになるため、また動かした際にセッションを開始
    if (this.sessionStartAt === undefined) {
      this.startSession()
    }
  }

  /**
   * 一定間隔 (10 秒) ごとに呼ばれるアクティビティチェック。意図としては「セッション継続中に
   * `INACTIVE_JUDGE_TIME` (180 秒) 以上の無操作があったとき、それまでの経過秒数を
   * `trackSessionTime(INACTIVE_JUDGE_TIME)` で記録してセッションを打ち切る」処理です。
   *
   * ⚠️ ただし現状の条件式 `this.sessionStartAt === undefined && ...` は意図と矛盾していると
   * 思われます。`sessionStartAt === undefined` の状態 (= セッションがまだ開始されていない、
   * または既に終了済) では、`trackSessionTime` を呼んでも内部の `getSessionTime()` が
   * undefined を返して early return するため、何も記録されません。本来は
   * `this.sessionStartAt !== undefined` (= セッション継続中) を条件にすべきで、現状この
   * メソッドは「死に処理」になっている可能性が高いです。
   *
   * 修正すると放置セッションの実績計上が変わり、利用時間グラフの集計値に影響しうるため、
   * 単純な条件反転で済むかどうかは別途検証が必要です。
   */
  private onActivityCheck() {
    // 180秒経過後にセッション開始時間(sessionStartAt)をundefinedに初期化すると、
    // それまでの操作時間がtrack(計上)されずに消えてしまうため、180秒放置された場合もtrackするようにする
    // (trackSessionTime()->getSessionTime()内でsessionStartAtはundefinedに初期化される)
    if (
      this.sessionStartAt === undefined &&
      dayjs().diff(this.lastActiveMotionAt, 'second', false) >= INACTIVE_JUDGE_TIME
    ) {
      this.trackSessionTime(INACTIVE_JUDGE_TIME)
    }
  }
}

export { Tracker }
